import { PaymentType } from '@afterpay/types'
import { Country } from '@afterpaytouch/portal-api/types'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  applePayLineItems,
  applePayTotalLineItem,
  captureApplePayPayment,
  CheckoutAppStore,
  checkoutDataSelector,
  consumerSelector,
  countrySelector,
  donationSelector,
  isRecoverableFlow,
  recoverableTerminalErrorsStateSelector,
  setApplePayFailure,
  setApplePayStatus,
  setupPayment,
  setupPurchase,
  startedCheckoutSelector,
  tokenSelector,
  validateMerchant,
  selectedPaymentTypeSelector,
  CheckoutAppDispatch,
  setApplePayCardType,
  applePayMPANFlowSelector,
  captureApplePayPaymentForMpanFlow,
  LineItem,
  isBillingAgreementApprovalProcessSelector,
  applePayBillingAgreementLineItem,
  upsertOnfileApplepay,
  setStartedOnfilePaymentMethod,
  traceIdSelector,
} from '../'
import {
  ApplePayError,
  ApplePayStatus,
  ApplePayToken,
  MPANPaymentSourceCaptureCardTypes,
  PaymentCaptureType,
  PaymentSource,
  PaymentSourceBillingAddress,
  PaymentSourceCaptureCardTypes,
  PaymentSourceCaptureMPANFlowRequest,
  PaymentSourceCaptureRequest,
  ResultCode,
  SupportedLocale,
  TopazSetupRequest,
} from '../../model'
import { TFunction } from 'i18next'
import { LogEvent, TrackingEvent } from '../../model/monitoring'
import { app } from '../../env'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { isPaymentsSourceCaptureApiError } from '../../utils/errors'

// TODO: Refactor this constant and store it somewhere globally.
const countriesWithBillingAddressRequirements = [Country.GB, Country.US, Country.CA]

const supportedCardTypes = ['credit', 'debit']
const supportedCardTypesPSC = [PaymentSourceCaptureCardTypes.CREDIT, PaymentSourceCaptureCardTypes.DEBIT]
const supportedCardTypesPCL = [PaymentSourceCaptureCardTypes.DEBIT]
const supportedMpanFlowCardTypesPSC = [MPANPaymentSourceCaptureCardTypes.CREDIT, MPANPaymentSourceCaptureCardTypes.DEBIT]
const supportedMpanFlowCardTypesPCL = [MPANPaymentSourceCaptureCardTypes.DEBIT]

export class ApplePayHelper {
  private static instance: ApplePayHelper
  dispatch: CheckoutAppDispatch
  session: ApplePaySession
  applePayPaymentRequest: ApplePayJS.ApplePayPaymentRequest
  store: CheckoutAppStore
  locale: SupportedLocale
  resolve: () => void
  reject: (reason?: any) => void
  logEvent: LogEvent
  isRecurringPayment: boolean
  isApplePaylaterWithApplePayRepayment: boolean

  private constructor(store: CheckoutAppStore, locale: SupportedLocale) {
    this.store = store
    this.dispatch = store.dispatch
    this.locale = locale
  }

  public static getInstance({ store, locale }): ApplePayHelper {
    if (typeof ApplePayHelper.instance === 'undefined') {
      ApplePayHelper.instance = new ApplePayHelper(store, locale)
    }
    return ApplePayHelper.instance
  }

  public initialise(
    t: TFunction,
    logEvent: LogEvent,
    paymentCaptureType: PaymentCaptureType = PaymentCaptureType.RECURRING,
    isApplePaylaterWithApplePayRepayment: boolean = false
  ): void {
    const isEnabledApplePayMPANFlow = applePayMPANFlowSelector(this.store.getState())
    const paymentRequest = isEnabledApplePayMPANFlow ? this.createMPANPaymentRequest(t, paymentCaptureType) : this.createPaymentRequest(t)

    this.applePayPaymentRequest = isApplePaylaterWithApplePayRepayment ? this.createApplePaylaterRepaymentRequest(t) : paymentRequest

    this.logEvent = logEvent
    this.isRecurringPayment = paymentCaptureType === PaymentCaptureType.RECURRING
    this.isApplePaylaterWithApplePayRepayment = isApplePaylaterWithApplePayRepayment

    try {
      this.session = new ApplePaySession(4, this.applePayPaymentRequest)
      this.session.onvalidatemerchant = this.validateMerchant.bind(this)
      this.session.onpaymentauthorized = this.handleAuthorizedPayment.bind(this)
      this.session.onshippingcontactselected = this.handleShippingContactSelected.bind(this)
      this.session.onshippingmethodselected = this.handleShippingMethodSelected.bind(this)
      this.session.onpaymentmethodselected = this.handlePaymentMethodSelected.bind(this)
      this.session.oncancel = this.handleCancel.bind(this)
    } catch (e) {
      this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
        errorMessage: e?.message,
        errorCode: e?.code,
        contactField: e?.contactField,
        customMessage: 'Session Initialization Failed',
      })
      this.dispatch(setApplePayFailure(ApplePayError.SessionInitializationFailed))
    }
  }

  public async startSession(): Promise<void> {
    return await new Promise((resolve, reject) => {
      if (typeof this.session === 'undefined') {
        this.dispatch(setApplePayFailure(ApplePayError.SessionInitializationFailed))
        return reject(ApplePayError.SessionInitializationFailed)
      }
      this.resolve = resolve
      this.reject = reject
      this.dispatch(setApplePayStatus(ApplePayStatus.SessionInitialized))
      try {
        this.session.begin()
      } catch (e) {
        this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
          errorMessage: e?.message,
          errorCode: e?.code,
          contactField: e?.contactField,
          customMessage: 'Beginning Session Failed',
        })
        this.dispatch(setApplePayFailure(ApplePayError.BeginningSessionFailed))
        reject(ApplePayError.BeginningSessionFailed)
      }
    })
  }

  private createPaymentRequest(t: TFunction): ApplePayJS.ApplePayPaymentRequest {
    const state = this.store.getState()
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { countryCode, merchant } = checkoutDataSelector(state)
    const { donationAmount } = donationSelector(state)
    const { puf, amount, currencyCode } = applePayTotalLineItem(state)
    const label = t('summary:applePay.total', { context: puf ? 'puf' : 'npuf' })
    const total: ApplePayJS.ApplePayLineItem = { label, amount, type: 'final' }
    const country = countrySelector(state)

    const lineItems = applePayLineItems(state).map(({ amount, count }) => ({
      label: t('summary:applePay.payments', { count }),
      amount,
    }))

    if (donationAmount > 0) {
      lineItems.push({
        label: t('summary:applePay.donation'),
        amount: donationAmount.toFixed(2),
      })
      total.amount = (parseFloat(total.amount) + donationAmount).toFixed(2)
    }
    const supportedCardList = getSupportedCountriesCardList(merchant.supportedCountries)

    const isBillingAddressRequired = countriesWithBillingAddressRequirements.includes(country)
    return {
      countryCode,
      currencyCode,
      supportedCountries: supportedCardList,
      lineItems,
      total,
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['visa', 'masterCard', 'discover'],
      requiredBillingContactFields: isBillingAddressRequired ? ['postalAddress'] : undefined,
    }
  }

  private createApplePaylaterRepaymentRequest(t: TFunction): ApplePayJS.ApplePayPaymentRequest {
    const state = this.store.getState()
    const { PORTAL_URL, TOKEN_NOTIFICATION_URL } = app

    const { countryCode, merchant } = checkoutDataSelector(state) ?? {}
    const { currencyCode } = applePayTotalLineItem(state)
    const country = countrySelector(state)

    const lineItems: ApplePayJS.ApplePayLineItem[] = [
      {
        label: t('summary:applePay.billingAgreement.label', { count: 0 }),
        amount: '0.00',
      },
    ]

    const recurringItem = {
      amount: '0.00',
      count: 0,
      startDate: new Date(),
    }

    const isBillingAddressRequired = countriesWithBillingAddressRequirements.includes(country)
    const supportedCardList = getSupportedCountriesCardList(merchant!.supportedCountries)

    return {
      countryCode: countryCode!,
      currencyCode,
      supportedCountries: supportedCardList,
      lineItems,
      total: {
        label: 'Afterpay',
        amount: '0.00',
        type: 'final',
      },
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['visa', 'masterCard', 'discover'],
      requiredBillingContactFields: isBillingAddressRequired ? ['postalAddress'] : undefined,
      recurringPaymentRequest: {
        paymentDescription: t('summary:applePay.billingAgreement.label'),
        regularBilling: {
          label: t('summary:applePay.billingAgreement.label'),
          amount: recurringItem.amount,
          paymentTiming: 'recurring',
          recurringPaymentStartDate: recurringItem.startDate,
        },
        billingAgreement: t('summary:applePay.billingAgreement.description'),
        managementURL: PORTAL_URL,
        ...(TOKEN_NOTIFICATION_URL && { tokenNotificationURL: TOKEN_NOTIFICATION_URL }),
      },
    }
  }

  private createMPANPaymentRequest(t: TFunction, paymentCaptureType: PaymentCaptureType): ApplePayJS.ApplePayPaymentRequest {
    const state = this.store.getState()
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { countryCode, merchant } = checkoutDataSelector(state)
    const { donationAmount } = donationSelector(state)
    const { puf, amount, currencyCode } = applePayTotalLineItem(state)
    const isBillingAgreementApprovalProcess = isBillingAgreementApprovalProcessSelector(state)

    const label = t('summary:applePay.total', { context: puf && !isBillingAgreementApprovalProcess ? 'puf' : 'npuf' })
    const total: ApplePayJS.ApplePayLineItem = { label, amount, type: 'final' }
    const country = countrySelector(state)

    const lineItems = isBillingAgreementApprovalProcess
      ? applePayBillingAgreementLineItem(state).map(({ amount, count }) => ({
          label: t('summary:applePay.billingAgreement.label', { count }),
          amount,
        }))
      : applePayLineItems(state).map(({ amount, count }) => ({
          label: t('summary:applePay.payments', { count }),
          amount,
        }))

    const recurringItem = isBillingAgreementApprovalProcess
      ? applePayBillingAgreementLineItem(state)[0]
      : applePayLineItems(state).find((item) => item.count > 1)

    if (donationAmount > 0) {
      lineItems.push({
        label: t('summary:applePay.donation'),
        amount: donationAmount.toFixed(2),
      })
      total.amount = (parseFloat(total.amount) + donationAmount).toFixed(2)
    }

    const recurringPaymentRequest = recurringItem !== undefined ? calculateRecurringPaymentRequest(t, recurringItem, isBillingAgreementApprovalProcess) : null

    const isBillingAddressRequired = countriesWithBillingAddressRequirements.includes(country)
    const isRecurringPaymentCaptureType = paymentCaptureType === PaymentCaptureType.RECURRING

    const supportedCardList = getSupportedCountriesCardList(merchant.supportedCountries)

    return {
      countryCode,
      currencyCode,
      supportedCountries: supportedCardList,
      lineItems,
      total,
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['visa', 'masterCard', 'discover'],
      requiredBillingContactFields: isBillingAddressRequired ? ['postalAddress'] : undefined,
      ...(isRecurringPaymentCaptureType && recurringPaymentRequest !== null && { recurringPaymentRequest }),
    }
  }

  private async validateMerchant(event: ApplePayJS.ApplePayValidateMerchantEvent): Promise<void> {
    try {
      const response = unwrapResult(await this.dispatch(validateMerchant(event)))
      this.session.completeMerchantValidation(response.sessionObject)
    } catch (e) {
      this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
        errorMessage: e?.message,
        errorCode: e?.code,
        contactField: e?.contactField,
        customMessage: 'Merchant Validation Failed',
      })
      this.dispatch(setApplePayFailure(ApplePayError.MerchantValidationFailed))
      this.session.abort()
      this.reject(ApplePayError.MerchantValidationFailed)
    }
  }

  private async handleAuthorizedPayment(applePayPaymentToken: ApplePayJS.ApplePayPaymentAuthorizedEvent): Promise<void> {
    const applePayToken = await this.exchangeApplePayToken(applePayPaymentToken.payment)
    if (applePayToken !== null) {
      const isTopazSuccessful = await this.setupTopaz(applePayToken)
      if (isTopazSuccessful) {
        this.session.completePayment({ status: 0 })
        this.resolve()

        if (this.isApplePaylaterWithApplePayRepayment) {
          void this.upsertOnfileApplePay()
        }
      } else {
        this.session.completePayment({ status: 1 })
        this.dispatch(setApplePayFailure(ApplePayError.PaymentRejected))
        this.reject(ApplePayError.PaymentRejected)
      }
    }
  }

  private async upsertOnfileApplePay(): Promise<void> {
    try {
      const traceId = traceIdSelector(this.store.getState())
      const result = unwrapResult(await this.dispatch(upsertOnfileApplepay({ traceId })))
      this.dispatch(setStartedOnfilePaymentMethod(result.onfilePaymentMethod))
    } catch (e) {
      this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
        errorMessage: e?.message,
        customMessage: 'Upsert onfile Apple Pay failure',
      })
    }
  }

  private async exchangeApplePayToken(applePayPayment: ApplePayJS.ApplePayPayment): Promise<ApplePayToken | null> {
    const isEnabledApplePayMPANFlow = applePayMPANFlowSelector(this.store.getState())
    let paymentSourceCaptureResponse
    let request
    this.logEvent(TrackingEvent.CHECKOUT_EXCHANGED_APPLE_PAY_TOKEN, { isEnabledApplePayMPANFlow })
    try {
      if (isEnabledApplePayMPANFlow) {
        request = this.getPaymentRequestForMpanFlow(applePayPayment, this.isRecurringPayment)
        paymentSourceCaptureResponse = unwrapResult(await this.dispatch(captureApplePayPaymentForMpanFlow(request)))
        if (paymentSourceCaptureResponse?.card_type === undefined && paymentSourceCaptureResponse?.paymentSourceId === undefined) {
          this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
            customMessage: 'Capture fallback, failed to exchange Apple Pay token',
          })
          this.dispatch(setApplePayFailure(ApplePayError.UnknownError))
          this.reject(ApplePayError.UnknownError)
          return null
        }
      } else {
        request = this.getPaymentRequest(applePayPayment)
        paymentSourceCaptureResponse = unwrapResult(await this.dispatch(captureApplePayPayment(request)))
      }

      const paymentSourceCaptureResult = isEnabledApplePayMPANFlow ? paymentSourceCaptureResponse : paymentSourceCaptureResponse?.data
      const state = this.store.getState()
      const paymentType = selectedPaymentTypeSelector(state)
      const validCardType = isEnabledApplePayMPANFlow
        ? isValidMpanCardType(paymentType, paymentSourceCaptureResult?.card_type)
        : isValidCardType(paymentType, paymentSourceCaptureResult?.card_type)
      if (validCardType) {
        const sourceId = isEnabledApplePayMPANFlow ? paymentSourceCaptureResult.payment_source_id : paymentSourceCaptureResult.source_id
        return { paymentSourceId: sourceId }
      } else {
        this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
          type: paymentSourceCaptureResult?.card_type,
          customMessage: 'Invalid PaymentSourceCapture card type',
        })
        this.dispatch(setApplePayFailure(ApplePayError.InvalidCardType))
        this.dispatch(setApplePayCardType(paymentSourceCaptureResult?.card_type))
        this.reject(ApplePayError.InvalidCardType)
        return null
      }
    } catch (e) {
      const isApiError = isPaymentsSourceCaptureApiError(e)
      const { code, errors, message } = isApiError ? e.error : { code: e?.code, message: e?.message, errors: e?.errors }

      this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
        errorMessage: message ?? 'Unknown error',
        errorCode: code ?? '',
        errorsList: errors ?? [],
        customMessage: 'Failed to exchange Apple Pay token',
        request: {
          refId: request.transaction_reference?.id,
        },
      })
      this.dispatch(setApplePayFailure(ApplePayError.UnknownError))
      this.reject(ApplePayError.UnknownError)
      return null
    }
  }

  private getSetupAction(applePayToken: ApplePayToken) {
    const state = this.store.getState()
    const { traceId: recoverableTraceId, status: recoverableStatus } = recoverableTerminalErrorsStateSelector(state)

    // @ts-expect-error: OPERATION BLEED STOPPER
    const payload: TopazSetupRequest = {
      ...applePayToken,
      ...(isRecoverableFlow(recoverableStatus) && { traceId: recoverableTraceId }),
    }

    if (isRecoverableFlow(recoverableStatus)) {
      return setupPayment.initiate(payload)
    }

    return setupPurchase(payload)
  }

  private async setupTopaz(applePayToken: ApplePayToken): Promise<boolean> {
    try {
      const setupAction = this.getSetupAction(applePayToken)
      // @ts-expect-error TODO: fix type error
      const response = unwrapResult(await this.dispatch(setupAction))
      return response.resultCode === ResultCode.OK
    } catch (e) {
      this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, {
        errorMessage: e?.message,
        errorCode: e?.errorCode,
        customMessage: 'Topaz setup failure',
      })
      return false
    }
  }

  private handleShippingContactSelected(): void {
    this.session.completeShippingContactSelection({
      newTotal: this.applePayPaymentRequest.total,
      newLineItems: this.applePayPaymentRequest.lineItems,
    })
  }

  private handleShippingMethodSelected(): void {
    this.session.completeShippingMethodSelection({
      newTotal: this.applePayPaymentRequest.total,
      newLineItems: this.applePayPaymentRequest.lineItems,
    })
  }

  private handlePaymentMethodSelected(event: ApplePayJS.ApplePayPaymentMethodSelectedEvent): void {
    if (supportedCardTypes.includes(event.paymentMethod.type)) {
      this.session.completePaymentMethodSelection({
        newTotal: this.applePayPaymentRequest.total,
        newLineItems: this.applePayPaymentRequest.lineItems,
      })
    } else {
      this.logEvent(TrackingEvent.CHECKOUT_APPLE_PAY_ERROR, { type: event.paymentMethod?.type, customMessage: 'Invalid card type' })
      this.dispatch(setApplePayFailure(ApplePayError.InvalidCardType))
      this.session.abort()
      this.reject(ApplePayError.InvalidCardType)
    }
  }

  private handleCancel(): void {
    this.dispatch(setApplePayStatus(ApplePayStatus.Cancelled))
    this.reject()
  }

  private getPaymentRequest({ token, billingContact }: ApplePayJS.ApplePayPayment): PaymentSourceCaptureRequest {
    const state = this.store.getState()
    const { uuid } = consumerSelector(state)
    const transactionToken = tokenSelector(state)
    const country = countrySelector(state)
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { paymentRoutingKey } = startedCheckoutSelector(state)
    const paymentSource = toPaymentSource(token)
    if (countriesWithBillingAddressRequirements.includes(country)) {
      // @ts-ignore: OPERATION BLEED STOPPER
      paymentSource.billing_address = toBillingAddress(billingContact)
    }
    return {
      // @ts-expect-error: OPERATION BLEED STOPPER
      user_id: uuid,
      transaction_reference: {
        id: transactionToken,
      },
      source: paymentSource,
      payment_routing_key: paymentRoutingKey,
    }
  }

  private getPaymentRequestForMpanFlow(
    { token, billingContact }: ApplePayJS.ApplePayPayment,
    isRecurringPayment: boolean
  ): PaymentSourceCaptureMPANFlowRequest {
    const state = this.store.getState()
    const { uuid } = consumerSelector(state)
    const transactionToken = tokenSelector(state)
    const country = countrySelector(state)

    const paymentSource = toPaymentSource(token, true)
    if (countriesWithBillingAddressRequirements.includes(country)) {
      // @ts-ignore: OPERATION BLEED STOPPER
      paymentSource.billing_address = toBillingAddress(billingContact)
    }
    return {
      // @ts-expect-error: OPERATION BLEED STOPPER
      user_id: uuid,
      transaction_reference: {
        id: transactionToken,
      },
      apple_pay: paymentSource,
      // represents whether the Apple Pay token should be stored
      // For once-off payments, the value should be false
      // For recurring/scheduled payments should be true
      stored: isRecurringPayment,
    }
  }
}

const calculateRecurringPaymentRequest = (
  t: TFunction,
  recurringItem: LineItem,
  isBillingAgreementApprovalProcess: boolean
): ApplePayJS.ApplePayRecurringPaymentRequest | undefined => {
  const { PORTAL_URL, TOKEN_NOTIFICATION_URL } = app

  if (recurringItem.startDate !== undefined) {
    const recurringItemLabel = isBillingAgreementApprovalProcess
      ? t('summary:applePay.billingAgreement.label')
      : t('summary:applePay.payments', { count: recurringItem.count })
    return {
      paymentDescription: recurringItemLabel,
      regularBilling: {
        label: recurringItemLabel,
        amount: recurringItem.amount,
        paymentTiming: 'recurring',
        recurringPaymentStartDate: recurringItem.startDate,
        ...(recurringItem.endDate !== undefined && {
          recurringPaymentEndDate: recurringItem.endDate,
          recurringPaymentIntervalUnit: 'day',
          recurringPaymentIntervalCount: recurringItem.paymentIntervalCount,
        }),
      },
      ...(isBillingAgreementApprovalProcess && {
        billingAgreement: t('summary:applePay.billingAgreement.description'),
      }),
      managementURL: PORTAL_URL,
      ...(TOKEN_NOTIFICATION_URL && { tokenNotificationURL: TOKEN_NOTIFICATION_URL }),
    }
  }
}

const toPaymentSource = (
  { paymentData, paymentMethod, transactionIdentifier }: ApplePayJS.ApplePayPaymentToken,
  isEnabledApplePayMPANFlow: boolean = false
): PaymentSource => {
  return {
    token: isEnabledApplePayMPANFlow ? Buffer.from(JSON.stringify(paymentData)).toString('base64') : JSON.stringify(paymentData),
    payment_network: paymentMethod?.network as 'MasterCard' | 'Visa' | 'Discover',
    payment_method_type: paymentMethod?.type as 'debit' | 'credit',
    payment_method_display_name: paymentMethod?.displayName,
    transaction_identifier: transactionIdentifier,
    // @ts-expect-error: OPERATION BLEED STOPPER
    billing_address: undefined,
  }
}

const toBillingAddress = (billingContact: ApplePayJS.ApplePayPaymentContact): PaymentSourceBillingAddress => {
  return {
    city: billingContact?.locality ?? '',
    state: billingContact?.administrativeArea ?? '',
    // @ts-ignore: OPERATION BLEED STOPPER
    address_line_1: billingContact?.addressLines[0] ?? '',
    // @ts-ignore: OPERATION BLEED STOPPER
    address_line_2: billingContact?.addressLines[1] ?? '',
    country: billingContact?.countryCode?.toUpperCase() ?? '',
    postal_code: billingContact?.postalCode ?? '',
  }
}

const isValidCardType = (paymentType: PaymentType | undefined, cardType: PaymentSourceCaptureCardTypes): boolean => {
  if (valueIsNotNullOrUndefined(paymentType) && paymentType === PaymentType.PCL) {
    return supportedCardTypesPCL.includes(cardType)
  }
  return supportedCardTypesPSC.includes(cardType)
}

const isValidMpanCardType = (paymentType: PaymentType | undefined, cardType: MPANPaymentSourceCaptureCardTypes): boolean => {
  if (valueIsNotNullOrUndefined(paymentType) && paymentType === PaymentType.PCL) {
    return supportedMpanFlowCardTypesPCL.includes(cardType)
  }
  return supportedMpanFlowCardTypesPSC.includes(cardType)
}

// apple sandbox cards are issued only in US, to allow testing in other non prod regions,
// updating supportingCountries https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/supportedcountries
// to include US
const getSupportedCountriesCardList = (supportedCountries: Country[] | undefined): Country[] | undefined => {
  const prodEnv = ['au-prod', 'eu-prod', 'us-prod']
  const shouldAllowApplePaySandboxCards = !prodEnv.includes(app.APP_ENVIRONMENT)

  if (shouldAllowApplePaySandboxCards) {
    const countries = supportedCountries ?? []
    const countriesWithUS = new Set([...countries, Country.US])
    return Array.from(countriesWithUS)
  }

  return supportedCountries
}
